import React, { createContext, useEffect, useState } from "react";

import { THEMES, ThemeType } from "constants/themes";

import { Language } from "types/Language";

export interface Settings {
  responsiveFontSizes?: boolean;
  roundedCorners?: boolean;
  theme?: ThemeType;
  language: Language;
}

export interface SettingsContextValue {
  settings: Settings;
  saveSettings: (update: Settings) => void;
}

const initialSettings: Settings = {
  responsiveFontSizes: true,
  roundedCorners: false,
  theme: THEMES.LIGHT,
  language: "en",
};

export const restoreSettings = (): Settings => {
  let settings: Settings = {
    ...initialSettings,
    theme: window.matchMedia("(prefers-color-scheme: dark)").matches
      ? THEMES.DARK
      : THEMES.LIGHT,
  };

  try {
    const storedData = window.localStorage.getItem("settings");
    if (storedData) {
      const parsedSettings = JSON.parse(storedData) as Settings;
      settings = { ...settings, ...parsedSettings };
    }
  } catch (err) {
    /*
     * If stored data is not a strigified JSON this will fail,
     * that's why we catch the error
     */
    // eslint-disable-next-line no-console
    console.error(err);
  }

  return settings;
};

export const storeSettings = (settings: Settings) => {
  window.localStorage.setItem("settings", JSON.stringify(settings));
};

export const SettingsContext = createContext<SettingsContextValue>({
  settings: initialSettings,
  saveSettings: () => {
    // Do nothing
  },
});

export const SettingsProvider: React.FC = (props) => {
  const { children } = props;
  const [settings, setSettings] = useState<Settings>(initialSettings);

  useEffect(() => {
    const restoredSettings = restoreSettings();

    if (restoredSettings) {
      setSettings(restoredSettings);
    }
  }, []);

  const saveSettings = (updatedSettings: Settings) => {
    setSettings(updatedSettings);
    storeSettings(updatedSettings);
  };

  const value = React.useMemo(
    () => ({
      settings,
      saveSettings,
    }),
    [settings]
  );

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
